import { useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { NotificationListItem } from '../../notification/view';
import { useAppRuntime, useWorkspaceContext, useWorkspaceRouteAdapter } from './context';
import { notificationListQueryKey, unreadNotificationStateQueryKey, useNotificationListQuery } from './data';
import { ActionError, BackHeader, CompactEmptyRow, RecoverableReadError, SectionLoading } from './ui';

export function NotificationsPage(): ReactNode {
  const routeAdapter = useWorkspaceRouteAdapter();
  const notifications = useNotificationListQuery();

  return (
    <div className="route-outlet" data-screen="notifications">
      <BackHeader
        title="通知"
        action="back-to-home"
        label="返回首页"
        onBack={() => routeAdapter.replaceMainTab('home')}
      />
      <NotificationListContent notifications={notifications} />
    </div>
  );
}

function NotificationListContent({
  notifications
}: {
  readonly notifications: ReturnType<typeof useNotificationListQuery>;
}): ReactNode {
  const openingGuard = useSingleNotificationOpenGuard();

  if (notifications.status === 'pending') {
    return <SectionLoading body="正在读取通知。" />;
  }

  if (notifications.status === 'error') {
    return (
      <RecoverableReadError
        error={notifications.error}
        retryAction="retry-notifications"
        onRetry={() => void notifications.refetch()}
      />
    );
  }

  if (notifications.data.length === 0) {
    return <CompactEmptyRow body="暂无通知。" />;
  }

  return (
    <section className="notification-list prototype-list" role="list" aria-label="通知列表">
      {notifications.data.map((notification) => (
        <NotificationRow
          notification={notification}
          hasOtherUnread={notifications.data.some(
            (candidate) => candidate.notificationId !== notification.notificationId && candidate.readAt === null
          )}
          openingGuard={openingGuard}
          key={notification.notificationId}
        />
      ))}
    </section>
  );
}

function NotificationRow({
  notification,
  hasOtherUnread,
  openingGuard
}: {
  readonly notification: NotificationListItem;
  readonly hasOtherUnread: boolean;
  readonly openingGuard: ReturnType<typeof useSingleNotificationOpenGuard>;
}): ReactNode {
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();
  const routeAdapter = useWorkspaceRouteAdapter();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const isRead = notification.readAt !== null;
  const readLabel = isRead ? '已读' : '未读';
  const timeLabel = formatNotificationDateTime(notification.createdAt);

  async function openNotification(): Promise<void> {
    if (!openingGuard.begin()) {
      return;
    }
    setPending(true);
    setActionError(null);
    try {
      const markedNotification = await service.markNotificationRead({
        workspaceContext,
        notificationId: notification.notificationId
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: notificationListQueryKey(workspaceContext), exact: true }),
        queryClient.invalidateQueries({ queryKey: unreadNotificationStateQueryKey(workspaceContext), exact: true })
      ]);
      queryClient.setQueryData(unreadNotificationStateQueryKey(workspaceContext), {
        hasUnread: isRead || hasOtherUnread
      });
      if (markedNotification?.targetType === 'song') {
        routeAdapter.openSongDetail(markedNotification.targetId);
      }
    } catch {
      setActionError('操作失败，请重试。');
    } finally {
      openingGuard.end();
      setPending(false);
    }
  }

  return (
    <div className="notification-list-item" role="listitem">
      <button
        type="button"
        className={`notification-row prototype-list-row ${isRead ? 'read' : 'unread'}`}
        data-notification-read={isRead ? 'true' : 'false'}
        disabled={pending}
        aria-busy={pending ? 'true' : undefined}
        aria-label={`${readLabel}，${notification.title}，${notification.body}，${timeLabel}`}
        onClick={() => void openNotification()}
      >
        <span className="notification-row-status" aria-hidden="true">
          {!isRead ? <span className="notification-unread-dot" /> : null}
        </span>
        <span className="notification-row-copy">
          <strong>{notification.title}</strong>
          <small>{readLabel}</small>
          <span>{notification.body}</span>
          {notification.updateNote ? (
            <small className="notification-update-note">{notification.updateNote}</small>
          ) : null}
          <time dateTime={notification.createdAt}>{timeLabel}</time>
        </span>
        <span className="row-chevron" aria-hidden="true">
          ›
        </span>
      </button>
      <ActionError message={actionError} />
    </div>
  );
}

function formatNotificationDateTime(isoTimestamp: string, now = new Date()): string {
  const date = new Date(isoTimestamp);
  if (Number.isNaN(date.getTime())) {
    throw new Error(`Invalid notification timestamp: ${isoTimestamp}`);
  }
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const notificationStart = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const dayDelta = Math.round((todayStart - notificationStart) / 86_400_000);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');
  if (dayDelta === 0) {
    return `今天 ${hour}:${minute}`;
  }
  if (dayDelta === 1) {
    return `昨天 ${hour}:${minute}`;
  }
  return `${month}月${day}日 ${hour}:${minute}`;
}

function useSingleNotificationOpenGuard(): {
  readonly begin: () => boolean;
  readonly end: () => void;
} {
  const pendingRef = useRef(false);
  return {
    begin: () => {
      if (pendingRef.current) {
        return false;
      }
      pendingRef.current = true;
      return true;
    },
    end: () => {
      pendingRef.current = false;
    }
  };
}
